import { useGetCustomPropertiesByIds } from 'src/api/custom-property'
import { ICustomProperty } from 'src/generated-types/customProperty'
import { formFieldsDefaultValues } from 'src/generated-types/form-field'
import { ILocality } from 'src/generated-types/locality'
import { IMainComponent } from 'src/generated-types/main-component'
import { IPosition } from 'src/generated-types/position'
import { IPositionType } from 'src/generated-types/position-type'
import i18n from 'src/i18n'
import { getCageLetter, getPositionName } from 'src/utils/function'
import {
  findCustomPropertyByKey,
  getCustomPropertyValue,
  getCustomPropertyValueByKeys,
  parseCustomPropertyValues,
} from 'src/utils/customPropertyValues'

/**
 * Default form initial values for locality reports (used when opening Add Report dialog
 * from locality dashboard or documents container). Pre-fills locality name and site number
 * when available.
 */
export const getLocalityReportFormInitialValues = (
  locality: ILocality | null | undefined,
): Record<string, string> => {
  if (!locality) return {}
  return {
    [formFieldsDefaultValues.siteLocationName]: locality.name ?? '',
    [formFieldsDefaultValues.facilityName]: locality.name ?? '',
    [formFieldsDefaultValues.siteNumber]: locality.locationNr ?? '',
  }
}

const getLocalizedTypeName = (mainComponent: IMainComponent): string => {
  const name = mainComponent.type?.name
  if (!name) return mainComponent.typeName ?? ''
  try {
    const parsed = JSON.parse(name)
    return parsed?.[i18n.language] ?? parsed?.no ?? parsed?.en ?? ''
  } catch {
    return name
  }
}

const getCagePlacement = (position: IPosition | undefined): string => {
  if (!position) return ''
  if (position.type !== IPositionType.Cage) return getPositionName(position)
  return `${getCageLetter(position.name)}${position.reference ? ` (${position.reference})` : ''}`
}

// Net type/function were imported from the old system as custom properties. Values are keyed by
// property id (label-keyed values remain for components that were never migrated).
const getImportedNetValue = (
  customValues: Record<string, unknown>,
  customProperties: ICustomProperty[] | undefined,
  label: string,
): string => {
  const property = findCustomPropertyByKey(label, customProperties)
  return (property && getCustomPropertyValue(customValues, property)) ?? getCustomPropertyValueByKeys(customValues, [label]) ?? ''
}

/**
 * Default form initial values for equipment (main component) reports. Resolves the same
 * placeholder tokens the old system used ("Anlegg", "Lokalitet" and "Ressurser" groups).
 */
export const getMainComponentReportFormInitialValues = (
  mainComponent: IMainComponent | null | undefined,
  locality: ILocality | null | undefined,
  customProperties?: ICustomProperty[],
): Record<string, string> => {
  const position = mainComponent?.position
  const customValues = parseCustomPropertyValues(mainComponent?.customPropertyValues)

  return {
    ...getLocalityReportFormInitialValues(locality),
    [formFieldsDefaultValues.facilityName]: position?.mooringName ?? position?.mooring?.name ?? '',
    [formFieldsDefaultValues.productionNumber]: mainComponent?.serialNumber ?? '',
    [formFieldsDefaultValues.orderNumber]: mainComponent?.orderNumber ?? '',
    [formFieldsDefaultValues.netType]:
      getImportedNetValue(customValues, customProperties, 'Net Type') ||
      (mainComponent ? getLocalizedTypeName(mainComponent) : ''),
    [formFieldsDefaultValues.netFunction]: getImportedNetValue(customValues, customProperties, 'Net Function'),
    [formFieldsDefaultValues.perimeter]: mainComponent?.circumference?.toString() ?? '',
    [formFieldsDefaultValues.cagePlacement]: getCagePlacement(position),
    [formFieldsDefaultValues.resourcesName]: mainComponent?.name ?? '',
    [formFieldsDefaultValues.positionName]: position ? getPositionName(position) : '',
  }
}

/**
 * Same as getMainComponentReportFormInitialValues, but loads the custom property definitions
 * needed to resolve the id-keyed net type/function values.
 */
export const useMainComponentReportFormInitialValues = (
  mainComponent: IMainComponent | null | undefined,
  locality: ILocality | null | undefined,
): Record<string, string> => {
  const propertyIds = Object.keys(parseCustomPropertyValues(mainComponent?.customPropertyValues))
    .map(Number)
    .filter(id => !isNaN(id))
  const { data: customProperties } = useGetCustomPropertiesByIds(mainComponent?.customerId as EntityId, propertyIds)
  return getMainComponentReportFormInitialValues(mainComponent, locality, customProperties)
}
